const sortByPosition = items => {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const positionA = Number.isFinite(Number(a?.position))
      ? Number(a?.position)
      : Number.MAX_SAFE_INTEGER;
    const positionB = Number.isFinite(Number(b?.position))
      ? Number(b?.position)
      : Number.MAX_SAFE_INTEGER;

    if (positionA !== positionB) return positionA - positionB;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
};

const getNumericValue = value => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getDetailTotal = detail => {
  if (detail?.column_total !== undefined && detail?.column_total !== null) {
    return detail.column_total;
  }

  return (
    getNumericValue(detail?.item_total) + getNumericValue(detail?.fringe_total)
  );
};

const getFringeNames = detail => {
  const fringes = [
    ...(Array.isArray(detail?.fringe) ? detail.fringe : []),
    ...(Array.isArray(detail?.linked_fringes) ? detail.linked_fringes : []),
  ];

  const names = fringes
    .map(
      fringe =>
        fringe?.display_name ||
        fringe?.label ||
        fringe?.name ||
        fringe?.fringe_name ||
        ''
    )
    .filter(Boolean);

  return [...new Set(names)].join('; ');
};

const formatPercentage = percentage => {
  const parsed = parseFloat(percentage);
  if (Number.isNaN(parsed)) return percentage || '';

  return parsed.toFixed(4).replace(/\.?0+$/, '');
};

const OVERTIME_DEFAULT_LABELS = ['1.5 X', 'Double OT', 'Triple OT', '4X OT'];

const getDetailColumns = budget => [
  { label: 'Item', getValue: detail => detail?.item },
  { label: 'Quantity', getValue: detail => detail?.quantity },
  { label: 'Unit', getValue: detail => detail?.unit },
  { label: 'Rate', getValue: detail => detail?.rate },
  ...OVERTIME_DEFAULT_LABELS.flatMap((defaultLabel, index) => {
    const slot = index + 1;
    return [
      {
        label: `OT${slot} Label`,
        getValue: () => budget?.[`ot${slot}_label`] ?? defaultLabel,
      },
      {
        label: `OT${slot} Quantity`,
        getValue: detail => detail?.[`ot${slot}_quantity`],
      },
      {
        label: `OT${slot} Multiplier`,
        getValue: () => budget?.[`ot${slot}_multiplier`],
      },
      {
        label: `OT${slot} Start`,
        getValue: () => budget?.[`ot${slot}_start`],
      },
      {
        label: `OT${slot} End`,
        getValue: () => budget?.[`ot${slot}_end`],
      },
    ];
  }),
  { label: 'Multiplier', getValue: detail => detail?.multiplier },
  { label: 'Multiplier Unit', getValue: detail => detail?.multiplier_unit },
  { label: 'Item Total', getValue: detail => detail?.item_total },
  { label: 'Fringes', getValue: detail => getFringeNames(detail) },
  { label: 'Fringe Total', getValue: detail => detail?.fringe_total },
  { label: 'Total', getValue: detail => getDetailTotal(detail) },
  { label: 'Actual', getValue: detail => detail?.actual },
  { label: 'Notes', getValue: detail => detail?.comment },
];

const escapeCsvValue = value => {
  if (value === null || value === undefined) return '';

  const stringValue =
    typeof value === 'object' ? JSON.stringify(value) : String(value);
  const normalizedValue = stringValue
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  if (/[",\n]/.test(normalizedValue)) {
    return `"${normalizedValue.replace(/"/g, '""')}"`;
  }

  return normalizedValue;
};

const collectLineItemRows = (
  lineItems,
  category,
  categoryTotal,
  pathItems = []
) => {
  return sortByPosition(lineItems).flatMap(lineItem => {
    const currentPath = [
      ...pathItems,
      {
        name: lineItem?.name || '',
        total: lineItem?.cost ?? '',
      },
    ];
    const detailRows = sortByPosition(
      lineItem?.swimlane_item_detail_sheets
    ).map(detail => ({
      category,
      categoryTotal,
      pathItems: currentPath,
      lineItemTotal: lineItem?.cost ?? '',
      detail,
    }));

    return [
      ...detailRows,
      ...collectLineItemRows(
        lineItem?.children,
        category,
        categoryTotal,
        currentPath
      ),
    ];
  });
};

export const getBudgetCsvRows = budgetData => {
  return sortByPosition(budgetData?.budget_swimlane).flatMap(swimlane => {
    const category = swimlane?.name || '';
    const categoryTotal = swimlane?.cost ?? '';
    const directRows = sortByPosition(swimlane?.swimlane_detail_sheets).map(
      detail => ({
        category,
        categoryTotal,
        pathItems: [],
        lineItemTotal: '',
        detail,
      })
    );

    return [
      ...directRows,
      ...collectLineItemRows(
        swimlane?.budget_swimlane_lineitem,
        category,
        categoryTotal
      ),
    ];
  });
};

const getContractualChargeRows = contractualCharges => {
  const sortedCharges = sortByPosition(contractualCharges);
  const contractualChargesTotal = sortedCharges.reduce(
    (total, charge) => total + getNumericValue(charge?.total_cost),
    0
  );

  return sortedCharges.map(charge => {
    const isPercentage = charge?.charge_type === 'percentage';
    const amount = charge?.total_cost ?? '';

    return {
      category: 'Contractual Charges',
      categoryTotal: contractualChargesTotal || '',
      pathItems: [],
      lineItemTotal: amount,
      detail: {
        item: charge?.name || '',
        quantity: amount ? '1' : '',
        unit: isPercentage ? 'Percentage' : 'Flat Fee',
        rate: amount,
        multiplier: amount ? '1' : '',
        multiplier_unit: '',
        item_total: amount,
        fringe_total: '',
        column_total: amount,
        comment: isPercentage
          ? `Percentage: ${formatPercentage(charge?.percentage)}%`
          : '',
      },
    };
  });
};

const getFilmIncentiveRows = filmIncentives => {
  const sortedIncentives = sortByPosition(filmIncentives);
  const hasIncomplete = sortedIncentives.some(
    incentive => !incentive?.estimate_complete
  );
  const totalCredits = sortedIncentives.reduce(
    (total, incentive) =>
      total +
      (incentive?.estimate_complete
        ? getNumericValue(incentive?.net_credit)
        : 0),
    0
  );

  return sortedIncentives.map(incentive => {
    const amount = incentive?.estimate_complete
      ? -getNumericValue(incentive?.net_credit)
      : '';
    const notes = [];
    if (incentive?.missing_exchange_rate) {
      notes.push('Incomplete: missing exchange rate');
    } else if (!incentive?.configuration_complete) {
      notes.push('Incomplete: no qualified-spend source');
    } else if (!incentive?.threshold_met) {
      notes.push('Below minimum qualified spend');
    }
    notes.push(
      `Estimate complete: ${incentive?.estimate_complete ? 'yes' : 'no'}`,
      `Rate: ${formatPercentage(incentive?.rate_percent)}%`,
      `Qualified spend: ${incentive?.qualified_spend ?? ''}`,
      `Gross credit: ${incentive?.gross_credit ?? ''}`,
      `Realization: ${formatPercentage(incentive?.realization_percent)}%`,
      `Estimated fees: ${incentive?.estimated_costs ?? 0}`
    );
    if (
      incentive?.minimum_qualified_spend !== null &&
      incentive?.minimum_qualified_spend !== undefined
    ) {
      notes.push(`Minimum spend: ${incentive.minimum_qualified_spend}`);
    }
    if (
      incentive?.maximum_credit !== null &&
      incentive?.maximum_credit !== undefined
    ) {
      notes.push(`Credit cap: ${incentive.maximum_credit}`);
    }

    return {
      category: 'Estimated Film Tax Credits',
      categoryTotal: hasIncomplete ? '' : -totalCredits,
      pathItems: [],
      lineItemTotal: amount,
      detail: {
        item: incentive?.name || '',
        quantity: incentive?.qualified_spend ?? '',
        unit: 'Qualified Spend',
        rate: incentive?.rate_percent ?? '',
        multiplier: incentive?.realization_percent ?? '',
        multiplier_unit: 'Percent Realization',
        item_total: amount,
        fringe_total: '',
        column_total: amount,
        comment: notes.join('; '),
      },
    };
  });
};

const getBudgetSummaryRows = ({
  budgetData,
  contractualCharges,
  filmIncentives,
}) => {
  if (!filmIncentives.length) return [];
  const grandTotal =
    getNumericValue(budgetData?.total_cost) +
    contractualCharges.reduce(
      (total, charge) => total + getNumericValue(charge?.total_cost),
      0
    );
  const incomplete = filmIncentives.some(
    incentive => !incentive?.estimate_complete
  );
  const totalCredits = filmIncentives.reduce(
    (total, incentive) =>
      total +
      (incentive?.estimate_complete
        ? getNumericValue(incentive?.net_credit)
        : 0),
    0
  );
  const summaryRow = (name, amount, comment = '') => ({
    category: 'Budget Summary',
    categoryTotal: '',
    pathItems: [],
    lineItemTotal: amount,
    detail: {
      item: name,
      quantity: '',
      unit: '',
      rate: '',
      multiplier: '',
      multiplier_unit: '',
      item_total: amount,
      fringe_total: '',
      column_total: amount,
      comment,
    },
  });

  return [
    summaryRow('Grand Total', grandTotal),
    summaryRow(
      'Net Production Cost',
      incomplete ? '' : grandTotal - totalCredits,
      incomplete ? 'Incomplete film tax credit estimate' : ''
    ),
  ];
};

export const buildBudgetCsv = ({
  budgetData,
  contractualCharges = [],
  filmIncentives = [],
  selectedBudget,
}) => {
  const rows = [
    ...getBudgetCsvRows(budgetData),
    ...getContractualChargeRows(contractualCharges),
    ...getFilmIncentiveRows(filmIncentives),
    ...getBudgetSummaryRows({
      budgetData,
      contractualCharges,
      filmIncentives,
    }),
  ];
  const maxDepth = rows.reduce(
    (max, row) => Math.max(max, row.pathItems.length),
    0
  );
  const subcategoryHeaders = Array.from({ length: maxDepth }, (_, index) =>
    index === 0 ? 'Subcategory' : `Subcategory ${index + 1}`
  );
  const detailColumns = getDetailColumns({
    ...(budgetData || {}),
    ...(selectedBudget || {}),
  });
  const headers = [
    'Category',
    'Category Total',
    ...subcategoryHeaders,
    'Line Item Total',
    ...detailColumns.map(column => column.label),
  ];
  const csvRows = rows.map(row => {
    const subcategoryValues = Array.from({ length: maxDepth }, (_, index) => {
      return row.pathItems[index]?.name || '';
    });

    return [
      row.category,
      row.categoryTotal,
      ...subcategoryValues,
      row.lineItemTotal,
      ...detailColumns.map(column => column.getValue(row.detail)),
    ];
  });

  return [headers, ...csvRows]
    .map(row => row.map(escapeCsvValue).join(','))
    .join('\n');
};

const getCsvFileName = budgetName => {
  const sanitizedName = String(budgetName || 'budget')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

  return `${sanitizedName || 'budget'}-budget.csv`;
};

export const downloadBudgetCsv = ({
  budgetData,
  selectedBudget,
  contractualCharges,
  filmIncentives,
}) => {
  const csv = buildBudgetCsv({
    budgetData,
    contractualCharges,
    filmIncentives,
    selectedBudget,
  });
  const blob = new Blob([`\uFEFF${csv}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = getCsvFileName(budgetData?.name || selectedBudget?.name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
